import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'

  useEffect(() => {
    // Back/Forward (POP): don't override — let the page render wherever it lands
    if (navType === 'POP') return;

    // Anchor/hash navigation: scroll to the target element
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'instant' }); return; }
    }

    // New page navigation: always start at the top
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, navType]);

  return null;
}
import { triggerSheetSync } from './lib/services-store';
import { getCachedSheetData, isCacheFresh } from './lib/sheets-sync';
import { AuthProvider } from './contexts/AuthContext';
import { AdminRoute } from './components/AdminRoute';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import GalleryPage from './pages/GalleryPage';
import BulkServicesPage from './pages/BulkServicesPage';
import SupportPage from './pages/SupportPage';
import RetroModsPage from './pages/RetroModsPage';
import BuybackPage from './pages/BuybackPage';
import ShopPage from './pages/ShopPage';
import CustomControllersPage from './pages/CustomControllersPage';
import MailInRepairsPage from './pages/MailInRepairsPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import CommunityPage from './pages/CommunityPage';
import PerformanceUpgradesPage from './pages/PerformanceUpgradesPage';
import LocalServicePage from './pages/LocalServicePage';
import BookRepairPage from './pages/BookRepairPage';
import RepairDatabasePage from './pages/RepairDatabasePage';
import RepairDevicePage from './pages/RepairDevicePage';
import RepairBoardPage from './pages/RepairBoardPage';
import RepairCasePage from './pages/RepairCasePage';
import RepairReferencePage from './pages/RepairReferencePage';
// ⚠️  Internal admin — not linked from any public page, not in nav
import AdminServicesPage            from './pages/admin/AdminServicesPage';
import AdminProductsPage            from './pages/admin/AdminProductsPage';
import AdminGBPPage                 from './pages/admin/AdminGBPPage';
import AdminPhoneRepairPreviewPage  from './pages/admin/AdminPhoneRepairPreviewPage';
import AdminReviewRequestsPage      from './pages/admin/AdminReviewRequestsPage';
import AdminInvoicesPage            from './pages/admin/AdminInvoicesPage';
import AdminImagesPage              from './pages/admin/AdminImagesPage';
import AdminEditSitePage            from './pages/admin/AdminEditSitePage';
import AdminRepairKBPage            from './pages/admin/AdminRepairKBPage';
import AdminRefEditorPage           from './pages/admin/AdminRefEditorPage';
import AdminControllersPage         from './pages/admin/AdminControllersPage';
import AdminNavigationPage          from './pages/admin/AdminNavigationPage';
import AdminGalleryPage             from './pages/admin/AdminGalleryPage';
import AdminTaxonomyPage            from './pages/admin/AdminTaxonomyPage';
import AdminAdSettingsPage          from './pages/admin/AdminAdSettingsPage';
// ── Phone & Tablet Repair section ──────────────────────────────────────────────
import PhoneRepairHomePage    from './pages/phone-repair/PhoneRepairHomePage';
import PhoneRepairCityPage    from './pages/phone-repair/PhoneRepairCityPage';
import DataRecoveryPage       from './pages/phone-repair/DataRecoveryPage';
import ScreenProtectionPage   from './pages/phone-repair/ScreenProtectionPage';

function App() {

  // ── Background sheet sync ────────────────────────────────────────────────────
  useEffect(() => {
    const maybeSync = () => {
      const cached = getCachedSheetData();
      if (!cached || !isCacheFresh(cached.fetchedAt)) {
        triggerSheetSync();
      }
    };
    maybeSync();
    window.addEventListener('focus', maybeSync);
    return () => window.removeEventListener('focus', maybeSync);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <Routes>

          {/* ── Admin login — public, no auth required ─────────────────────── */}
          <Route path="/admin/login" element={<AdminLoginPage />} />

          {/* ── Admin area — protected by AdminRoute ───────────────────────── */}
          <Route path="/admin" element={<AdminRoute />}>
            <Route index element={<Navigate to="/admin/edit-site" replace />} />
            <Route path="services"               element={<AdminServicesPage />} />
            <Route path="products"               element={<AdminProductsPage />} />
            <Route path="gbp"                    element={<AdminGBPPage />} />
            <Route path="phone-repair-preview"   element={<AdminPhoneRepairPreviewPage />} />
            <Route path="reviews"                element={<AdminReviewRequestsPage />} />
            <Route path="invoices"               element={<AdminInvoicesPage />} />
            <Route path="images"                 element={<AdminImagesPage />} />
            <Route path="edit-site"              element={<AdminEditSitePage />} />
            <Route path="repair-knowledge-base"              element={<AdminRepairKBPage />} />
            <Route path="repair-knowledge-base/refs"         element={<AdminRefEditorPage />} />
            <Route path="repair-knowledge-base/ref/:refId"   element={<AdminRefEditorPage />} />
            <Route path="repair-knowledge-base/taxonomy"     element={<AdminTaxonomyPage />} />
            <Route path="repair-knowledge-base/ads"          element={<AdminAdSettingsPage />} />
            <Route path="repair-knowledge-base/:id"          element={<AdminRepairKBPage />} />
            <Route path="controllers"            element={<AdminControllersPage />} />
            <Route path="navigation"             element={<AdminNavigationPage />} />
            <Route path="gallery"                element={<AdminGalleryPage />} />
          </Route>

          {/* ── Public site ────────────────────────────────────────────────── */}
          <Route path="*" element={
            <div className="min-h-screen bg-[#0D0D0D]">
              <Header />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/bulk-services" element={<BulkServicesPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/retro-mods" element={<RetroModsPage />} />
                <Route path="/buyback" element={<BuybackPage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/custom-controllers" element={<CustomControllersPage />} />
                <Route path="/mail-in-repairs" element={<MailInRepairsPage />} />
                <Route path="/book-repair" element={<BookRepairPage />} />
                <Route path="/repair-database" element={<RepairDatabasePage />} />
                <Route path="/repair-database/ref/:refId" element={<RepairReferencePage />} />
                <Route path="/repair-database/:deviceSlug" element={<RepairDevicePage />} />
                <Route path="/repair-database/:deviceSlug/:boardOrCase" element={<RepairBoardPage />} />
                <Route path="/repair-database/:deviceSlug/:boardSlug/:caseSlug" element={<RepairCasePage />} />
                <Route path="/blog" element={<BlogIndexPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/community" element={<CommunityPage />} />
                <Route path="/performance-upgrades" element={<PerformanceUpgradesPage />} />
                {/* ── Phone & Tablet Repair section (preview — not in nav yet) ── */}
                <Route path="/phone-repair" element={<PhoneRepairHomePage />} />
                <Route path="/phone-repair/data-recovery" element={<DataRecoveryPage />} />
                <Route path="/phone-repair/screen-protection" element={<ScreenProtectionPage />} />
                <Route path="/phone-repair/:city" element={<PhoneRepairCityPage />} />
                {/* Local service area pages */}
                <Route path="/:city" element={<LocalServicePage />} />
              </Routes>
              <Footer />
            </div>
          } />

        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
